// The ONE typed boundary over the untyped rig maths. Every cast in the new path lives here.
// Notes: ~/comments/aos-threejs-poc/rigMath_typed_facade_one_boundary.md

import {
  buildMouthSubspace as buildMouthSubspaceJs,
  buildRegionalSubspaces as buildRegionalSubspacesJs,
  subspaceRetention as subspaceRetentionJs,
  regionKey,
} from "../rig/mouthSubspace.js";
import { trainedLimits as trainedLimitsJs } from "../../lib/rigBoard.js";
import { buildLiveMouthDrive as buildLiveMouthDriveJs } from "../rig/liveRigRegions.js";
import {
  resolveEyeFollowDrive as resolveEyeFollowDriveJs,
  applyEyeFollowRig as applyEyeFollowRigJs,
} from "../rig/eyeFollowRig.js";
import { arkitToIsaacRig as arkitToIsaacRigJs } from "../rig/arkitToIsaacRig.js";
import { resolveRigRange as resolveRigRangeJs } from "../rig/rigBoardSource.js";

/** A fitted subspace: decompose a rig in the trained basis and pull it back. */
export interface Subspace {
  projectInto(rig: Float32Array): number;
  /** Components kept, on a single whole-rig fit. */
  k?: number;
  dims?: number;
  /** Per-region fits, when the scope is `region`. */
  regions?: { region: string; dims: number; k: number }[];
}

export interface SubspaceInput {
  presets: Float32Array;
  rows: number;
  cols: number;
  extraPoses: Float32Array[];
}

/** One `[lo,hi]` for the whole board, or one per gathered control. */
export type RigClampRange = readonly [number, number] | number[][] | null;

export interface LiveMouthDrive {
  source: string;
  closedRow: number;
  openRow: number;
  subset: unknown[];
}

export interface EyeFollowDrive {
  slots: { slot: number; axis: string }[];
  refs?: unknown;
}

// These four modules are plain JavaScript with no declarations, and TypeScript infers
// their parameter types from their own defaults — `extraPoses = []` infers `never[]`, so
// a real argument cannot be passed. Rather than cast at a dozen call sites, the boundary
// is declared ONCE here and the rest of the pipeline layer stays strict.
type BuildGlobal = (input: SubspaceInput & { mouthIdx: number[] }) => Subspace | null;
type BuildRegional = (input: SubspaceInput & { names: string[]; groupBy: (name: string) => string }) => Subspace | null;
type Retention = (subspace: Subspace, base: Float32Array | null, poses: Float32Array[]) => { median: number; min: number } | null;
type TrainedLimits = (presets: Float32Array, dim: number, range: readonly [number, number], pad?: number) => number[][];
type BuildMouth = (input: { scene: unknown; names: string[]; presets: Float32Array; dim: number; rest: Float32Array }) => LiveMouthDrive | null;
type ResolveEyes = (input: { rigNames: string[]; presets: Float32Array | null; dim: number; eyesIdx: unknown; base: Float32Array | null }) => EyeFollowDrive | null;

export const buildGlobalSubspace = buildMouthSubspaceJs as unknown as BuildGlobal;
export const buildRegionalSubspaces = buildRegionalSubspacesJs as unknown as BuildRegional;
export const subspaceRetention = subspaceRetentionJs as unknown as Retention;
export const trainedLimits = trainedLimitsJs as unknown as TrainedLimits;
export const buildLiveMouthDrive = buildLiveMouthDriveJs as unknown as BuildMouth;
type ApplyEyeFollow = (rig: Float32Array, drive: EyeFollowDrive, overlay52: ArrayLike<number>, lo: number, hi: number) => void;

export const resolveEyeFollowDrive = resolveEyeFollowDriveJs as unknown as ResolveEyes;
export const applyEyeFollowRig = applyEyeFollowRigJs as unknown as ApplyEyeFollow;
type ArkitToRig = (
  arkit52: Float32Array, gather: ArrayLike<number>, out: Float32Array,
  scratch188?: Float32Array, rest?: ArrayLike<number> | null, range?: RigClampRange,
) => Float32Array;
/** The bundle's declared control domain, or null when it declares none. */
type ResolveRigRange = (scene: unknown) => readonly [number, number] | null;

export const arkitToIsaacRig = arkitToIsaacRigJs as unknown as ArkitToRig;
export const resolveRigRange = resolveRigRangeJs as unknown as ResolveRigRange;
export { regionKey };
